import { useEffect, useState } from 'react';
import { Modal } from '../../../../core/components/templates/Modal';
import { Alert } from '../../../../core/components/atoms/Alert';
import { Typography } from '../../../../core/components/molecules/Typography';
import { add, format } from 'date-fns';
import { TextArea } from '../../../../core/components/atoms/Textarea';
import { Select, iOption } from '../../../../core/components/atoms/Select';
import { Button } from '../../../../core/components/atoms/Button';
import { Icon } from '../../../../core/components/atoms/Icon';
import { useSendFeedbackReview } from '../../hooks/useSendFeedbackReview';
import { Toast, toast } from '../../../../core/components/atoms/Toast';

interface IReviewModal {
  isOpen: boolean;
  onClose: (action: string) => void;
  correctionId: string;
  dataCorrected: Date;
}

export function ModalReview({
  isOpen,
  onClose,
  correctionId,
  dataCorrected,
}: IReviewModal) {
  const { mutate, isPending, isError, isSuccess, data } =
    useSendFeedbackReview();

  const [rate, setRate] = useState<number>(0);
  const [motivation, setMotivation] = useState<iOption>();
  const [description, setDescription] = useState<string>('');

  const dayFormat = () => {
    const date = new Date(dataCorrected);

    const updated = add(date, { days: 7 });

    return format(updated, 'dd/MM/yyyy');
  };

  const motivations = [
    {
      label: 'Comentários pouco específicos.',
      value: 'Comentários pouco específicos.',
    },
    {
      label: 'Comentários incompatíveis com a nota.',
      value: 'Comentários incompatíveis com a nota.',
    },
    {
      label: 'Erro do corretor (indique o comentário).',
      value: 'Erro do corretor (indique o comentário).',
    },
    {
      label: 'Nota incompatível com o texto (justifique o motivo).',
      value: 'Nota incompatível com o texto (justifique o motivo).',
    },
    {
      label: 'Problemas na visualização da correção.',
      value: 'Problemas na visualização da correção.',
    },
    { label: 'Outro motivo (explique).', value: 'Outro motivo (explique).' },
  ];

  const onSubmit = () => {
    if(!rate || rate === 0 || (rate < 3 && (!motivation || !description))) {
      toast({
        variant: 'error',
        title: 'Erro ao enviar review',
        message: 'Preencha todos os campos',
      });
      
      return;
    }

    const body = {
      essayFeedbackId: correctionId,
      rating: rate.toString(),
      ratingComment: description,
      ratingReason: motivation?.value.toString() || '',
    };

    mutate(body);
  };

  useEffect(() => {
    if (isSuccess && data) {
      onClose('reload');
    }
  }, [isPending]);

  useEffect(() => {
    if (isError) {
      toast({
        variant: 'error',
        title: 'Erro ao enviar review',
        message:
          'Não foi possivel enviar sua avaliação, entre em contato caso o problema persistir.',
      });
    }
  }, [isError]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => onClose('close')}
      position='center'
      className='w-1/2 p-0'
      title='Avaliar correção'
    >
      <Toast />
      <div className='flex flex-col gap-xl px-5 py-4'>
        <Alert
          variant='warning'
          message={`Você pode avaliar a correção até dia ${dayFormat()}`}
        />
        <div className='flex flex-col items-center justify-center gap-sm'>
          <Typography weight='bold'>Deixe sua avaliação</Typography>
          <div className='flex flex-row gap-md'>
            {[...Array(5)].map((__, i) => (
              <Icon
                key={i}
                Icon='star'
                type='material'
                variant='rounded'
                className={`
                  ${i + 1 <= rate ? 'filled text-mono-color-darkest' : ''} 
                  text-mono-color-darkest cursor-pointer text-[85px]`
                }
                onClick={() => setRate(i + 1)}
              />
            ))}
          </div>
        </div>
        {rate > 0 && rate <= 3 && (
          <Select
            options={motivations}
            label='Selecione um motivo'
            onChange={(value) => setMotivation(value)}
            value={motivation}
            className='w-full'
          />
        )}
        <TextArea
          placeholder={`${rate >= 4 ? 'Envie uma mensagem para nós (Opcional)' : 'Envie uma mensagem para nós'}`}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className='w-full'
          rows={5}
          maxLength={1024}
        />
        <div className='f1lex-row flex items-center justify-end gap-sm'>
          <Button
            full
            loading={isPending}
            variant='outlined'
            onClick={() => onClose('close')}
          >
            Cancelar
          </Button>
          <Button full loading={isPending} onClick={() => onSubmit()}>
            Enviar avaliação
          </Button>
        </div>
      </div>
    </Modal>
  );
}
